import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import {
  AspectRatio,
  Flex,
  Grid,
  GridItem,
  Box,
  Stack,
  Skeleton,
  VisuallyHidden,
} from '@chakra-ui/react';
import { Card } from '@outdoorly/card';
import { Container } from '@outdoorly/container';
import { Text } from '@outdoorly/text';
import gql from 'graphql-tag';
import React, { useEffect, useState } from 'react';
import { PageLayout } from '../../components/page-layout';
import NextLink from 'next/link';
import NextImage from 'next/image';
import { initializeApollo } from '../../lib/auth/apollo';
import { getNav } from '../../lib/get-nav';
import { getBlurUrl, getImageLoader } from '@outdoorly/util';
import { makeNetworkStatus } from '../../hooks/make-network-status';
import { useAuth } from '../../providers/auth';
import { NextSeo } from 'next-seo';
const BRANDS_QUERY = gql`
  query BrandList($first: Int!, $after: String) {
    brands(first: $first, after: $after, onlyVisibleOnStorefront: true) {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id
          name
          slug
          cardHeroUrl
          whiteLogoTransparentUrl
        }
      }
    }
  }
`;

const COLLECTIONS_QUERY = gql`
  query CollectionList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: CollectionFilterInput
    $sortBy: CollectionSortingInput
    $onlyVisibleOnStorefront: Boolean
  ) {
    collections(
      first: $first
      after: $after
      last: $last
      before: $before
      filter: $filter
      sortBy: $sortBy
      onlyVisibleOnStorefront: $onlyVisibleOnStorefront
    ) {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id
          name
          cardHero {
            url
          }
          cardLogo {
            url
          }
          slug
        }
      }
    }
  }
`;

const BrandCard = ({
  slug,
  name,
  backgroundImageUrl,
  logoUrl,
  as = 'a',
  isCollectionSlug,
}) => {
  return (
    <NextLink
      href={isCollectionSlug ? `collections/${slug}` : `/brands/${slug}`}
      passHref
    >
      <GridItem as={as} aria-label={name}>
        <AspectRatio ratio={16 / 10} overflow="hidden">
          <Card backgroundColor="scheme.bgPrimaryInverse" position="relative">
            {backgroundImageUrl && (
              <NextImage
                loader={getImageLoader(backgroundImageUrl)}
                src={backgroundImageUrl}
                blurDataURL={getBlurUrl(backgroundImageUrl)}
                layout="fill"
                objectFit="cover"
                objectPosition="center center"
                sizes="(min-width: 62em) 30vw, (min-width: 48em) ,100vw"
                quality={100}
                className="smooth-filter"
                placeholder="blur"
              />
            )}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              backgroundColor="black"
              opacity={0.2}
            />
            {logoUrl ? (
              <Flex position="relative" width="50%" height="full">
                <NextImage
                  loader={getImageLoader(logoUrl)}
                  layout="fill"
                  objectFit="contain"
                  src={logoUrl}
                />
              </Flex>
            ) : (
              <Text
                color="scheme.textPrimaryInverse"
                size="4xl"
                fontWeight="bold"
                as="h3"
                maxW="90%"
              >
                <Box isTruncated>{name}</Box>
              </Text>
            )}
          </Card>
        </AspectRatio>
        <VisuallyHidden>{name}</VisuallyHidden>
      </GridItem>
    </NextLink>
  );
};

BrandCard.propTypes = {
  backgroundImageUrl: PropTypes.string,
  logoUrl: PropTypes.string,
  name: PropTypes.string,
  slug: PropTypes.string,
  as: PropTypes.string,
  isCollectionSlug: PropTypes.bool,
};

export async function getStaticProps() {
  const apolloClient = initializeApollo();

  await getNav(apolloClient);

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
    },
    revalidate: 100,
  };
}

const BrandSkeletons = () => {
  return (
    <Grid
      templateColumns={[
        'repeat(1, minmax(0, 1fr))',
        null,
        'repeat(2, minmax(0, 1fr))',
        'repeat(3, minmax(0, 1fr))',
      ]}
      gap={4}
    >
      {[...Array(50)].map((_, index) => (
        <AspectRatio key={index} ratio={16 / 10} overflow="hidden">
          <Skeleton borderRadius="lg" />
        </AspectRatio>
      ))}
    </Grid>
  );
};

const BrandsPage = () => {
  const { isInitializing } = useAuth();
  const [allCards, setAllCards] = useState(null);

  const { data, fetchMore, networkStatus } = useQuery(BRANDS_QUERY, {
    variables: { first: 100, after: null },
    notifyOnNetworkStatusChange: true,
    skip: isInitializing,
  });

  const {
    data: collectionsData,
    fetchMore: collectionsFetchMore,
    networkStatus: collectionsNetworkStatus,
  } = useQuery(COLLECTIONS_QUERY, {
    variables: {
      first: 100,
      after: null,
      filter: {
        actingAsBrand: true,
      },
      onlyVisibleOnStorefront: true,
    },
    notifyOnNetworkStatusChange: true,
    skip: isInitializing,
  });

  const { loading, refetching } = makeNetworkStatus(networkStatus);
  const { loading: collectionsLoading, refetching: collectionsRefetching } =
    makeNetworkStatus(collectionsNetworkStatus);

  useEffect(() => {
    if (!loading && data?.brands.pageInfo.hasNextPage === true) {
      fetchMore({
        variables: {
          first: 100,
          after: data?.brands.pageInfo.endCursor,
        },
      });
    }
  }, [data, fetchMore, loading]);

  useEffect(() => {
    if (
      !collectionsLoading &&
      collectionsData?.collections.pageInfo.hasNextPage === true
    ) {
      collectionsFetchMore({
        variables: {
          first: 100,
          after: collectionsData?.collections.pageInfo.endCursor,
          filter: {
            actingAsBrand: true,
          },
          onlyVisibleOnStorefront: true,
        },
      });
    }
  }, [collectionsData, collectionsFetchMore, collectionsLoading]);

  useEffect(() => {
    const noMoreItems =
      data?.brands.pageInfo.hasNextPage === false &&
      collectionsData?.collections.pageInfo.hasNextPage === false;
    if (
      noMoreItems &&
      (collectionsData?.collections.edges.length ?? -1) >= 0 &&
      (data?.brands.edges.length ?? -1) >= 0
    ) {
      //sort brands + collections as brands
      const brandCards = data?.brands.edges
        .map((edge) => edge.node)
        .map((node) => ({
          name: node.name,
          element: {
            key: node.id,
            logoUrl: node.whiteLogoTransparentUrl,
            backgroundImageUrl: node.cardHeroUrl,
            name: node.name,
            slug: node.slug,
            isCollectionSlug: false,
          },
        }));
      const collectionCards = collectionsData?.collections.edges
        .map((edge) => edge.node)
        .map((node) => ({
          name: node.name,
          element: {
            key: node.id,
            logoUrl: node.cardLogo?.url,
            backgroundImageUrl: node.cardHero?.url,
            name: node.name,
            slug: node.slug,
            isCollectionSlug: true,
          },
        }));
      const brandAndCollectionCards = brandCards
        .concat(collectionCards)
        .sort((a, b) => a.name.localeCompare(b.name));
      setAllCards(brandAndCollectionCards);
    }
  }, [data, collectionsData]);

  return (
    <PageLayout backgroundColor="scheme.bgSecondary">
      <NextSeo
        title={`Brands | Outdoorly`}
        description={
          'We have exclusive pro discounts on top outdoor brands. Your next outdoor adventure starts here.'
        }
        openGraph={{
          url: `https://www.outdoorly.com/brands`,
          title: 'Brands',
          description:
            'We have exclusive pro discounts on top outdoor brands. Your next outdoor adventure starts here.',
          images: data?.brands?.edges.map((node) => ({
            url: node?.cardHeroUrl,
          })),
        }}
      />
      <Container paddingTop={6} as={Stack} spacing={8}>
        {loading ||
        refetching ||
        collectionsLoading ||
        collectionsRefetching ? (
          <BrandSkeletons />
        ) : (
          <Grid
            templateColumns={[
              'repeat(1, minmax(0, 1fr))',
              null,
              'repeat(2, minmax(0, 1fr))',
              'repeat(3, minmax(0, 1fr))',
            ]}
            gap={4}
          >
            {allCards &&
              allCards.map(({ element: { key, ...rest } }) => (
                <BrandCard key={key} {...rest} />
              ))}
          </Grid>
        )}
      </Container>
    </PageLayout>
  );
};

export default BrandsPage;
